// KnowledgeTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Plus, BookOpen, Scale, Edit, Eye } from 'lucide-react';

export const KnowledgeTab = ({
  knowledge = [],
  guidelines = [],
  onAddKnowledge,
  onEditKnowledge,
  onAddGuideline,
  onEditGuideline
}) => {
  return (
    <TabsContent value="knowledge" className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">AI Knowledge & Guidelines</h3>
          <p className="text-sm text-muted-foreground">Manage the knowledge base and rules for AI agents</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onAddKnowledge} data-testid="add-knowledge-btn">
            <BookOpen className="h-4 w-4 mr-2" />
            Add Knowledge
          </Button>
          <Button variant="outline" onClick={onAddGuideline} data-testid="add-guideline-btn">
            <Scale className="h-4 w-4 mr-2" />
            Add Guideline
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Knowledge Base
            </CardTitle>
            <CardDescription>Playbooks, policies, and reference material for AI agents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {knowledge.map(k => (
                <div key={k.id} className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => onEditKnowledge(k)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        {k.title}
                        <Eye className="h-3 w-3 text-muted-foreground" />
                      </h4>
                      <Badge variant="outline" className="text-xs mt-1">{k.category}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={k.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                        v{k.version}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {knowledge.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-3">No knowledge entries yet</p>
                  <Button size="sm" onClick={onAddKnowledge}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Entry
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Guidelines
            </CardTitle>
            <CardDescription>Rules with enforcement levels for AI behavior</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {guidelines.map(g => (
                <div 
                  key={g.id} 
                  className={`p-3 border rounded-lg cursor-pointer hover:shadow-sm transition-all ${
                    g.enforcement === 'strict' ? 'border-red-200 bg-red-50 hover:bg-red-100' : 
                    g.enforcement === 'warning' ? 'border-amber-200 bg-amber-50 hover:bg-amber-100' :
                    'hover:bg-slate-50'
                  }`}
                  onClick={() => onEditGuideline(g)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{g.name || g.title}</h4>
                        <Badge className={
                          g.enforcement === 'strict' ? 'bg-red-500 text-white' :
                          g.enforcement === 'warning' ? 'bg-amber-500 text-white' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {g.enforcement || g.rule_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{g.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{g.category}</Badge>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {guidelines.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <Scale className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-3">No guidelines defined yet</p>
                  <Button size="sm" onClick={onAddGuideline}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Guideline
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
};

export default KnowledgeTab;
